import type { EventBranding, EventWithDetails } from '@/types/event';

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
const COMPANY_LOGOS_PATH = '/storage/v1/object/public/company-logos/';

const IMAGE_SIGNATURES: { mime: string; bytes: number[] }[] = [
  { mime: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
  { mime: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47] },
  { mime: 'image/gif', bytes: [0x47, 0x49, 0x46] },
  { mime: 'image/webp', bytes: [0x52, 0x49, 0x46, 0x46] },
];

export const DEFAULT_BRAND_PRIMARY = '#152238';
export const DEFAULT_BRAND_SECONDARY = '#C9A84C';

export function isValidHexColor(value: string): boolean {
  return HEX_COLOR_RE.test(value.trim());
}

export function normalizeHexColor(value: string, fallback: string): string {
  const trimmed = value.trim();
  if (isValidHexColor(trimmed)) return trimmed.toUpperCase();
  return fallback;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  if (!isValidHexColor(hex)) return null;
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/** Contraste WCAG entre dos colores hex (ratio >= 4.5 recomendado para texto normal). */
export function getContrastRatio(colorA: string, colorB: string): number | null {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);
  if (!a || !b) return null;
  const l1 = relativeLuminance(a);
  const l2 = relativeLuminance(b);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function meetsContrastMinimum(
  foreground: string,
  background: string,
  minRatio = 4.5
): boolean {
  const ratio = getContrastRatio(foreground, background);
  return ratio !== null && ratio >= minRatio;
}

/** Sanitiza URL de logo: solo https y bucket company-logos. */
export function sanitizeCompanyLogoUrl(url: string | null | undefined): string | null {
  if (!url?.trim()) return null;
  const trimmed = url.trim();
  if (!trimmed.startsWith('https://')) return null;
  if (!trimmed.includes(COMPANY_LOGOS_PATH)) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function isCompanyLogoStorageUrl(url: string): boolean {
  return sanitizeCompanyLogoUrl(url) !== null;
}

/** Valida tipo real por magic bytes (no confiar en extensión/MIME del cliente). */
export async function detectImageMimeFromFile(file: File): Promise<string | null> {
  const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  for (const sig of IMAGE_SIGNATURES) {
    if (sig.bytes.every((byte, i) => header[i] === byte)) {
      if (sig.mime === 'image/webp') {
        const webp = String.fromCharCode(...header.slice(8, 12));
        return webp === 'WEBP' ? 'image/webp' : null;
      }
      return sig.mime;
    }
  }
  return null;
}

export function parseEventMetaFromDescription(description?: string | null): {
  description: string;
  meta: Record<string, unknown>;
} {
  if (!description) return { description: '', meta: {} };
  const match = description.match(/<!-- BIAVENT_META:(.*?) -->/s);
  if (!match) return { description, meta: {} };
  try {
    const meta = JSON.parse(match[1]) as Record<string, unknown>;
    const cleanDesc = description.replace(/<!-- BIAVENT_META:.*? -->/s, '').trim();
    return { description: cleanDesc, meta };
  } catch {
    return { description, meta: {} };
  }
}

export function resolveEventBranding(event: EventWithDetails): EventBranding {
  if (event.orgBranding) {
    return {
      logoUrl: event.orgBranding.logoUrl ?? undefined,
      primaryColor: normalizeHexColor(event.orgBranding.primaryColor, DEFAULT_BRAND_PRIMARY),
      secondaryColor: normalizeHexColor(event.orgBranding.secondaryColor, DEFAULT_BRAND_SECONDARY),
      source: 'organization',
    };
  }

  const { meta } = parseEventMetaFromDescription(event.description);
  const primary =
    typeof meta.primaryColor === 'string' ? meta.primaryColor : DEFAULT_BRAND_PRIMARY;
  const secondary =
    typeof meta.accentColor === 'string' ? meta.accentColor : DEFAULT_BRAND_SECONDARY;
  const logo =
    typeof meta.companyLogo === 'string' ? sanitizeCompanyLogoUrl(meta.companyLogo) : null;

  return {
    logoUrl: logo ?? undefined,
    primaryColor: normalizeHexColor(primary, DEFAULT_BRAND_PRIMARY),
    secondaryColor: normalizeHexColor(secondary, DEFAULT_BRAND_SECONDARY),
    source: Object.keys(meta).length > 0 ? 'event_meta' : 'default',
  };
}

export function brandingToCssVars(branding: EventBranding): Record<string, string> {
  return {
    '--event-brand-primary': branding.primaryColor,
    '--event-brand-secondary': branding.secondaryColor,
  };
}
